import { type FC, memo, useMemo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Heading, Tag, TagKind, TagVariant } from "@stubhub/broadway-web";
import { useContent, useFormattedContent } from "src/shared/components/content";
import { TruncateText } from "src/shared/components/TruncateText";
import { PERFORMER_IMAGE_SIZES } from "src/website-apps/responsive/stubhub-performer/components/page-body/constants";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { FAVORITES_BUTTON2_VARIANTS } from "src/website-apps/shared/components/FavoritesV2Buttons/constants";
import { FavoritesIconSize } from "src/website-apps/shared/components/FavoritesV2Buttons/FavoritesV2Button/types";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import {
  ButtonsContainer,
  StyledPortalFavoritesButtonV2,
  StyledPortalShareButton,
} from "src/website-apps/shared/components/hero-banner/ImmersiveHeroBanner/ImmersiveHeroBanner.styled";
import { Image, ImageSize } from "src/website-apps/shared/components/image";
import { GRID_FILTER_TYPES } from "src/website-apps/shared/components/immersive-performer/eventlist-filters/constants";
import { SHARING_ORIGINS } from "src/website-apps/shared/components/stubhub-sharing/SharingOrigins";
import { HERO_BANNER_CONTENT_ID } from "src/website-apps/shared/constants/header";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import {
  getPerformerResidencyContext,
  hasPerformerSurfaceEvents,
} from "src/website-apps/shared/utils/performerGridSurface";

const outerStyle = css({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  columnGap: "primitives.400",
  width: "full",
});

const leftColumnStyle = css({
  display: "flex",
  flexDirection: "column",
  rowGap: "primitives.200",
  flexGrow: "[1]",
  flexShrink: "[1]",
  flexBasis: "[0%]",
  minWidth: "[0]",
});

const subheaderRowStyle = css({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "primitives.200",
});

const eventInfoRowStyle = css({
  display: "flex",
  flexDirection: "row",
  columnGap: "primitives.800",
  alignItems: "flex-start",
});

const eventInfoColumnStyle = css({
  display: "flex",
  flexDirection: "column",
  rowGap: "primitives.100",
  flexGrow: "[1]",
  flexShrink: "[1]",
  flexBasis: "[0%]",
  minWidth: "[0]",
});

const imageStyle = css({
  width: "[400px]",
  height: "[208px]",
  objectFit: "cover",
  borderStartStartRadius: "element.default",
  borderStartEndRadius: "element.default",
  borderEndStartRadius: "element.default",
  borderEndEndRadius: "element.default",
  display: "block",
  flexShrink: "[0]",
});

const performerTitleStyle = css({
  marginBlockStart: "primitives.0",
  marginBlockEnd: "primitives.0",
});

const metaStyle = css({
  fontSize: "primitives.350",
  color: "text.subtle",
});

const locationStyle = css({
  fontSize: "primitives.400",
  fontWeight: "medium",
  color: "text",
});

const bioStyle = css({
  fontSize: "primitives.350",
  color: "text.subtle",
});

/**
 * Residency performer featured section rendered in the desktop header banner
 * when calendarRenderMode === PerformerViewMode.Residency.
 * Renders full-width (DesktopHeaderBanner suppresses BannerTitle for this mode).
 */
export const PerformerFeaturedSection: FC = memo(() => {
  const model = usePerformerPageModelData();
  const { categoryName, categoryTitle, portalImageUrl, portalImageSrcSet, categoryId, aggregateFavorites } =
    model.categorySummary;
  const residencyContext = getPerformerResidencyContext(model.performerGridSurface);
  const performerDateRange = residencyContext?.performerDateRange;
  const residencyVenueSubtitle = residencyContext?.venueSubtitle;
  const { totalUnfilteredEventsCount } = model;
  const bio = model.categoryMetadata?.common?.bio;
  const hasEvents = hasPerformerSurfaceEvents(model.performerGridSurface);

  const [state] = useEventsGridContext();
  const showCount = totalUnfilteredEventsCount;
  const isParking = useMemo(() => state.filters.gridFilterType === GRID_FILTER_TYPES.PARKING, [state]);
  const untilContent = useFormattedContent("cid$#4245664#", [String(performerDateRange)]); // Until {date}

  /** \{categoryName\} parking */
  const parkingTitle = useFormattedContent("cid$#3789327#", [categoryName ?? ""]);
  const displayTitle = isParking ? parkingTitle : (categoryTitle ?? categoryName);
  const residencyTagContent = useContent("cid$#4245661#"); // Residency
  const showsCountSingular = useFormattedContent("cid$#4245659#", [String(showCount)]); // "{count} show available"
  const showsCountPlural = useFormattedContent("cid$#4245660#", [String(showCount)]); // "{count} shows available"
  const showsCountLabel = showCount === 1 ? showsCountSingular : showsCountPlural;

  return (
    <div className={outerStyle}>
      <div className={leftColumnStyle}>
        <div className={subheaderRowStyle}>
          <Tag kind={TagKind.Brand} variant={TagVariant.Outline}>
            {residencyTagContent}
          </Tag>
          {performerDateRange && <span className={metaStyle}>{untilContent}</span>}
        </div>
        <div className={eventInfoRowStyle}>
          <div className={eventInfoColumnStyle}>
            {displayTitle && (
              <Heading
                id={HERO_BANNER_CONTENT_ID}
                as="h1"
                variant="display"
                size="medium"
                color="text"
                className={performerTitleStyle}
              >
                {displayTitle}
              </Heading>
            )}
            {!!residencyVenueSubtitle && (
              <div className={locationStyle}>
                {residencyVenueSubtitle}
                {!!showCount && <> &bull; {showsCountLabel}</>}
              </div>
            )}
            {bio && (
              <div className={bioStyle}>
                <TruncateText isModal modalTitle={categoryName ?? ""} maxLines={2} text={bio} seeMoreOnNewLine />
              </div>
            )}
          </div>
          <ButtonsContainer $width={80} $marginTop={0}>
            <StyledPortalFavoritesButtonV2
              authRedirectUrl=""
              entityId={categoryId}
              entityType={ENTITY_TYPES.CATEGORY}
              aggregateFavorites={aggregateFavorites ?? 0}
              variant={FAVORITES_BUTTON2_VARIANTS.LIGHT}
              notificationIconSize={FavoritesIconSize.Large}
              stopPropagation
              preventDefault
              $position="absolute"
              $right={40}
              $onTransparent
            />
            {hasEvents && (
              <StyledPortalShareButton
                sharingOrigin={SHARING_ORIGINS.PERFORMER}
                data={{ title: categoryName ?? "" }}
                $topOffset={20}
                $onTransparent
              />
            )}
          </ButtonsContainer>
        </div>
      </div>
      <Image
        alt={categoryName ?? ""}
        src={portalImageUrl}
        srcSet={portalImageSrcSet ?? undefined}
        sizes={PERFORMER_IMAGE_SIZES}
        size={ImageSize.PortalImage}
        loading="eager"
        fetchPriority="high"
        className={imageStyle}
      />
    </div>
  );
});

PerformerFeaturedSection.displayName = "PerformerFeaturedSection";
